/**
 * Game Context
 *
 * Manages all game state and provides actions to components.
 * Handles LocalStorage persistence automatically.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type {
  PuzzleData,
  GameState,
  GameContextValue,
} from '../types';
import {
  loadPuzzle as loadPuzzleFile,
  parsePuzzle,
  saveGameState,
  loadGameState as loadStoredGameState,
  createInitialGameState,
  isCorrectAnswer,
  getTodayDate,
  HINT_COST,
  INCORRECT_GUESS_COST,
  SECOND_INCORRECT_GUESS_COST,
  MAX_GUESSES_PER_COLUMN,
  CORRECT_MAIN_ANSWER_POINTS,
} from '../utils';
import { useGameSync } from '../hooks/useGameSync';

const GameContext = createContext<GameContextValue | null>(null);

interface GameProviderProps {
  children: React.ReactNode;
  /** If provided, use this puzzle directly instead of fetching from Supabase/file */
  initialPuzzle?: { puzzleText: string; date: string } | { puzzleData: PuzzleData };
}

export function GameProvider({ children, initialPuzzle }: GameProviderProps) {
  // Parse synchronous initialPuzzle eagerly so Board sees puzzle on the very first render
  // (avoids a blank frame between LoginBoard unmount and Board's first paint).
  const syncPuzzle = initialPuzzle
    ? ('puzzleData' in initialPuzzle
        ? initialPuzzle.puzzleData
        : parsePuzzle(initialPuzzle.puzzleText, initialPuzzle.date))
    : null;

  const [puzzle, setPuzzle] = useState<PuzzleData | null>(syncPuzzle);
  const [gameState, setGameState] = useState<GameState>(() => {
    if (syncPuzzle) {
      const puzzleDate = syncPuzzle.date;
      const savedState = loadStoredGameState(puzzleDate);
      if (savedState) {
        const mergedRevealedClues = savedState.state.revealedClues.map((colArr, c) =>
          colArr.map((saved, r) => saved || (syncPuzzle.grid[c]?.[r]?.isRevealed ?? false))
        );
        return {
          ...savedState.state,
          revealedClues: mergedRevealedClues,
          isAnimating: false,
          activeDialog: 'none' as const,
          activeDialogColumn: null,
          activeDialogRow: null,
          date: puzzleDate,
          puzzleUuid: syncPuzzle.id ?? null,
        };
      }
      const initialState = createInitialGameState(puzzleDate);
      const updatedRevealedClues = initialState.revealedClues.map((colArray, colIndex) =>
        colArray.map((_, rowIndex) => syncPuzzle.grid[colIndex][rowIndex].isRevealed)
      );
      return { ...initialState, revealedClues: updatedRevealedClues, puzzleUuid: syncPuzzle.id ?? null };
    }
    return createInitialGameState(getTodayDate());
  });
  const hadLocalStateRef = useRef(false);

  // Lockout animation state (credits hit zero)
  const [lockoutAnimating, setLockoutAnimating] = useState(false);
  const prevCreditsRef = useRef(gameState.credits);

  // Callback for cross-device restore from Supabase
  const handleRemoteStateLoaded = useCallback((remoteState: Partial<GameState>) => {
    if (!hadLocalStateRef.current) {
      setGameState(prev => ({
        ...prev,
        ...remoteState,
        // Always reset UI state
        isAnimating: false,
        activeDialog: 'none' as const,
        activeDialogColumn: null,
        activeDialogRow: null,
      }));
    }
  }, []);

  // Supabase sync hook (fire-and-forget, errors logged silently)
  useGameSync({
    gameState,
    puzzleLoaded: puzzle !== null,
    hadLocalState: hadLocalStateRef.current,
    onRemoteStateLoaded: handleRemoteStateLoaded,
  });

  // Load puzzle on mount — skipped when initialPuzzle was parsed synchronously above
  useEffect(() => {
    if (syncPuzzle) {
      // Already initialized synchronously in useState — just mark hadLocalState
      hadLocalStateRef.current = !!loadStoredGameState(syncPuzzle.date);
      return;
    }
    const initPuzzle = async () => {
      try {
        const today = getTodayDate();
        const loadedPuzzle = await loadPuzzleFile(today);
        setPuzzle(loadedPuzzle);

        const puzzleDate = loadedPuzzle.date;

        // Try to load saved state
        const savedState = loadStoredGameState(puzzleDate);
        if (savedState) {
          hadLocalStateRef.current = true;
          const mergedRevealedClues = savedState.state.revealedClues.map((colArr, c) =>
            colArr.map((saved, r) => saved || (loadedPuzzle.grid[c]?.[r]?.isRevealed ?? false))
          );
          setGameState(_prev => ({
            ...savedState.state,
            revealedClues: mergedRevealedClues,
            isAnimating: false,
            activeDialog: 'none',
            activeDialogColumn: null,
            activeDialogRow: null,
            date: puzzleDate,
            puzzleUuid: loadedPuzzle.id ?? null,
          }));
        } else {
          hadLocalStateRef.current = false;
          const initialState = createInitialGameState(puzzleDate);
          const updatedRevealedClues = initialState.revealedClues.map((colArray, colIndex) =>
            colArray.map((_, rowIndex) => loadedPuzzle.grid[colIndex][rowIndex].isRevealed)
          );
          setGameState({
            ...initialState,
            revealedClues: updatedRevealedClues,
            puzzleUuid: loadedPuzzle.id ?? null,
          });
        }
      } catch (error) {
        console.error('Failed to initialize puzzle:', error);
      }
    };

    initPuzzle();
  }, []);

  // Auto-save game state whenever it changes
  useEffect(() => {
    if (puzzle) {
      saveGameState(gameState.date, gameState);
    }
  }, [gameState, puzzle]);

  // Detect credits dropping to zero → trigger lockout animation
  useEffect(() => {
    const prev = prevCreditsRef.current;
    prevCreditsRef.current = gameState.credits;

    if (prev > 0 && gameState.credits === 0 && !gameState.finalAnswerSubmitted
        && gameState.solvedColumns.length < 4) {
      setLockoutAnimating(true);
    }
  }, [gameState.credits, gameState.finalAnswerSubmitted, gameState.solvedColumns.length]);

  const clearLockout = useCallback(() => {
    setLockoutAnimating(false);
  }, []);

  // Actions
  const revealHint = useCallback((column: number, row: number) => {
    setGameState(prev => {
      // Validate indices
      if (!puzzle || column < 0 || column >= 4 || row < 0 || row >= 6) {
        console.error('Invalid hint indices:', { column, row });
        return prev;
      }

      // Check if already revealed
      if (prev.revealedClues[column][row]) {
        console.warn('Hint already revealed');
        return prev;
      }

      // Check credits — hints blocked when credits <= 1 (buying would end the game)
      if (prev.credits <= HINT_COST) {
        console.warn('Not enough credits (hints blocked at 1 credit)');
        return prev;
      }

      // Update state
      const newRevealedClues = prev.revealedClues.map((col, c) =>
        c === column
          ? col.map((revealed, r) => (r === row ? true : revealed))
          : col
      );

      // Track this as a purchased hint (credit spent, not auto-revealed)
      const newPurchasedHints = prev.purchasedHints.map((col, c) =>
        c === column
          ? col.map((purchased, r) => (r === row ? true : purchased))
          : col
      );

      return {
        ...prev,
        credits: Math.max(0, prev.credits - HINT_COST),
        revealedClues: newRevealedClues,
        purchasedHints: newPurchasedHints,
      };
    });
  }, [puzzle]);

  const submitColumnGuess = useCallback((column: number, row: number, answer: string) => {
    setGameState(prev => {
      if (!puzzle) return prev;

      // Get the correct answer from the clue text at this specific cell
      const clueText = puzzle.grid[column]?.[row]?.text;
      if (!clueText) {
        console.error('Invalid cell:', column, row);
        return prev;
      }

      // Build list of all accepted answers for this column:
      // 1. The specific hidden clue text
      // 2. Column answer primary + alternates (if defined)
      const acceptedAnswers = [clueText];
      const colAnswer = puzzle.columnAnswers?.[column];
      if (colAnswer) {
        acceptedAnswers.push(colAnswer.primary, ...colAnswer.alternates);
      }

      const correct = isCorrectAnswer(answer, acceptedAnswers);

      if (correct) {
        // Correct guess - no credit cost, reveal ALL clues in this column
        const newRevealedClues = prev.revealedClues.map((col, c) =>
          c === column
            ? col.map(() => true) // Reveal all rows (0-5) in this column
            : col
        );

        return {
          ...prev,
          solvedColumns: [...prev.solvedColumns, column],
          columnAnswers: prev.columnAnswers.map((ans, idx) =>
            idx === column ? answer : ans
          ),
          guessAttempts: {
            ...prev.guessAttempts,
            [column]: (prev.guessAttempts[column] || 0) + 1,
          },
          revealedClues: newRevealedClues,
        };
      } else {
        // Incorrect guess — cost depends on attempt number
        const currentAttempts = prev.guessAttempts[column] || 0;
        const isSecondWrong = currentAttempts >= 1;
        const baseCost = isSecondWrong ? SECOND_INCORRECT_GUESS_COST : INCORRECT_GUESS_COST;
        const creditDeduction = Math.min(baseCost, prev.credits);
        const newAttempts = currentAttempts + 1;

        // Force-reveal the column when:
        // 1. 2nd wrong guess (MAX_GUESSES_PER_COLUMN reached), OR
        // 2. Credits drop to 0 AND this is the last unsolved column
        //    (lets the player still attempt the final answer at 0 credits)
        const newCredits = Math.max(0, prev.credits - creditDeduction);
        const isLastColumn = prev.solvedColumns.length === 3;
        const forceReveal = newAttempts >= MAX_GUESSES_PER_COLUMN
          || (newCredits === 0 && isLastColumn);

        const newRevealedClues = forceReveal
          ? prev.revealedClues.map((col, c) =>
              c === column ? col.map(() => true) : col
            )
          : prev.revealedClues;

        return {
          ...prev,
          credits: Math.max(0, prev.credits - creditDeduction),
          guessAttempts: {
            ...prev.guessAttempts,
            [column]: newAttempts,
          },
          incorrectGuesses: {
            ...prev.incorrectGuesses,
            [column]: [...(prev.incorrectGuesses[column] || []), answer],
          },
          // Force-reveal: mark column as solved so it counts toward the 4-column requirement
          ...(forceReveal ? {
            solvedColumns: [...prev.solvedColumns, column],
            revealedClues: newRevealedClues,
          } : {}),
        };
      }
    });
  }, [puzzle]);

  const submitFinalAnswer = useCallback((answer: string) => {
    setGameState(prev => {
      if (!puzzle) return prev;

      const correctAnswers = [
        puzzle.finalAnswer.primary,
        ...puzzle.finalAnswer.alternates,
      ];

      const correct = isCorrectAnswer(answer, correctAnswers);

      const finalScore = correct
        ? CORRECT_MAIN_ANSWER_POINTS + prev.credits
        : 0;

      return {
        ...prev,
        finalAnswerSubmitted: answer,
        gameWon: correct,
        finalScore,
        endTime: Date.now(),
      };
    });
  }, [puzzle]);

  const endGameCreditsExhausted = useCallback(() => {
    setGameState(prev => ({
      ...prev,
      finalAnswerSubmitted: '__CREDITS_EXHAUSTED__',
      gameWon: false,
      finalScore: 0,
      endTime: Date.now(),
    }));
  }, []);

  const openDialog = useCallback((type: 'column-guess' | 'final-answer', column?: number, row?: number) => {
    setGameState(prev => ({
      ...prev,
      activeDialog: type,
      activeDialogColumn: type === 'column-guess' ? (column ?? null) : null,
      activeDialogRow: type === 'column-guess' ? (row ?? null) : null,
    }));
  }, []);

  const closeDialog = useCallback(() => {
    setGameState(prev => ({
      ...prev,
      activeDialog: 'none',
      activeDialogColumn: null,
      activeDialogRow: null,
    }));
  }, []);

  const resetGame = useCallback(() => {
    if (!puzzle) return;
    const date = puzzle.date; // use puzzle's own date, not today
    const initialState = createInitialGameState(date);
    const updatedRevealedClues = initialState.revealedClues.map((colArray, colIndex) =>
      colArray.map((_, rowIndex) => puzzle.grid[colIndex][rowIndex].isRevealed)
    );
    const resetState = { ...initialState, revealedClues: updatedRevealedClues, puzzleUuid: puzzle.id ?? null };
    setGameState(resetState);
    // Save synchronously so WelcomePanel reads fresh state from localStorage
    // (the auto-save useEffect would run too late — after WelcomePanel renders)
    saveGameState(date, resetState);
  }, [puzzle]);

  const contextValue: GameContextValue = {
    // Puzzle data
    puzzle,
    loadPuzzle: loadPuzzleFile,

    // Game state
    credits: gameState.credits,
    revealedClues: gameState.revealedClues,
    solvedColumns: gameState.solvedColumns,
    guessAttempts: gameState.guessAttempts,
    finalAnswerSubmitted: gameState.finalAnswerSubmitted !== null,
    finalAnswerText: gameState.finalAnswerSubmitted,
    gameWon: gameState.gameWon,
    finalScore: gameState.finalScore,

    // Actions
    revealHint,
    submitColumnGuess,
    submitFinalAnswer,
    endGameCreditsExhausted,

    // UI state
    isAnimating: gameState.isAnimating,
    activeDialog: gameState.activeDialog,
    activeDialogColumn: gameState.activeDialogColumn,
    activeDialogRow: gameState.activeDialogRow,

    // UI actions
    openDialog,
    closeDialog,
    resetGame,

    // Lockout
    lockoutAnimating,
    clearLockout,
  };

  return (
    <GameContext.Provider value={contextValue}>
      {children}
    </GameContext.Provider>
  );
}

/**
 * Hook to access game context
 * Must be used within GameProvider
 */
export function useGameContext(): GameContextValue {
  const context = useContext(GameContext);

  if (!context) {
    throw new Error('useGameContext must be used within GameProvider');
  }

  return context;
}
